/**
 * Web wiring for the pre-announcement attention tone.
 *
 * The pure tone policy (setting allowlist, asset-ref parsing, tone queue
 * items, one-tone-per-takeover guard) now lives in @workspace/player-core so
 * the native shells share it. This module keeps only the web-specific part —
 * resolving a preset tone to the bundled asset path under the Vite base — and
 * re-exports the rest so the web player's existing importers are unchanged.
 */

import {
  resolveToneUrlWith,
  type AnnouncementTone,
} from "@workspace/player-core";

export {
  ANNOUNCEMENT_TONES,
  ASSET_TONE_PREFIX,
  assetToneMediaAssetId,
  isAssetToneRef,
  isToneItemId,
  readAnnouncementTone,
  shouldPrependTone,
  toneItemFor,
  toneQueueEntry,
  type AnnouncementTone,
  type AnnouncementToneSetting,
  type AssetToneRef,
} from "@workspace/player-core";

/** Public URL of a bundled preset tone asset, honoring the app base path. */
export function toneUrl(toneId: AnnouncementTone): string {
  // Optional access: import.meta.env only exists under Vite (not node --test).
  const base = import.meta.env?.BASE_URL ?? "/";
  const withSlash = base.endsWith("/") ? base : `${base}/`;
  return `${withSlash}assets/tones/${toneId}.mp3`;
}

/**
 * The playable URL for the configured tone, or null when no tone should play.
 * Presets always resolve to the bundled asset path; asset tones require the
 * server-hydrated presigned URL (DeviceInfo.announcementToneUrl) — when it is
 * missing (asset deleted, uploads unconfigured, pre-first-sync) the tone is
 * skipped and the announcement plays without it.
 */
export function resolveToneUrl(
  settings: unknown,
  hydratedUrl: string | null | undefined,
): string | null {
  return resolveToneUrlWith(settings, hydratedUrl, toneUrl);
}
